/* @flow */

import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, toJS } from 'mobx';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Link } from 'react-router-dom';
import { RouterStore } from 'mobx-react-router';
import RequireForAccess, { ReactAccessConsumer } from 'react-access';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import AccountStore from 'stores/AccountStore';
import ContractStore from 'stores/ContractStore';
import BrandStore from 'stores/BrandStore';
import type { RoleList } from 'permissions/defs';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import TitleActionButtonRow from 'components/TitleActionButtonRow';
import type { ActionButtonSpec } from 'components/TitleActionButtonRow';

import styles from './PageTitle.module.scss';

export type TitleComponent = {
  text: string,
  count?: number,
  onClick?: () => void,
  to?: string,
  loading?: boolean,
};

export type ComputeTitleFnData = {
  accountStore?: AccountStore,
  brandStore?: BrandStore,
  contractStore?: ContractStore,
  routing?: RouterStore,
};
export type ComputeTitleFn = {
  dependencies: ('accountStore' | 'brandStore' | 'contractStore' | 'routing')[],
  compute: (data: ComputeTitleFnData) => TitleComponent | TitleComponent[],
};

export type TitleProp = TitleComponent | TitleComponent[] | ComputeTitleFn;
export type PageTitleProps = {
  title: TitleProp,
  actionButtons?: ActionButtonSpec[],
  titleSlot?: React.Node,
  /**
   * Base 64 encoded
   */
  logoPath?: string[],
};

type InjectedProps = {
  accountStore: AccountStore,
  contractStore: ContractStore,
  brandStore: BrandStore,
  routing: RouterStore,
} & TranslatorProps;

@translate()
@inject('routing', 'accountStore', 'contractStore', 'brandStore')
@observer
export default class PageTitle extends ComponentWithInjectedProps<
  PageTitleProps,
  {},
  InjectedProps
> {
  renderTitleText = () => {
    const { title, titleSlot, t } = this.props;

    let _title: TitleComponent[] = [];
    if (typeof title.compute === 'function') {
      const data = {};
      for (const dep of title.dependencies) {
        data[dep] = this.props[dep];
      }

      _title = [title.compute(data)].flat();
    } else {
      _title = [title].flat();
    }

    return (
      <div className={styles.titleWrapper}>
        {_title.map((titleComponent, index) => {
          const titleElement = `${t(titleComponent.text)}${
            typeof titleComponent.count === 'number' ? ` (${titleComponent.count})` : ''
          }`;

          let child = [titleElement];
          const isLink = titleComponent.to || titleComponent.onClick;

          if (isLink) {
            const linkProps = { to: '#' };

            if (titleComponent.to) {
              linkProps.to = titleComponent.to;
            }

            if (titleComponent.onClick) {
              linkProps.onClick = titleComponent.onClick;
            }

            child = [
              <Link
                style={{ display: 'inline-block' }}
                key={`page-title-link-${index}`}
                {...linkProps}
              >
                {titleElement}
              </Link>,
            ];
          }

          if (index < _title.length - 1) {
            child.push(<span key={`page-title-divider-${index}`}>|</span>);
          }

          return (
            <h1
              className={titleComponent.loading ? styles.loading : undefined}
              key={`page-title-component-${index}`}
            >
              {child}
            </h1>
          );
        })}

        {titleSlot}
      </div>
    );
  };

  renderLeftButtons = () =>
    this.renderButtons(
      (this.props.actionButtons || []).filter(button => button.position === 'left')
    );

  renderRightButtons = () =>
    this.renderButtons(
      (this.props.actionButtons || []).filter(
        button => !button.position || button.position === 'right'
      )
    );

  renderButtons = (buttons: ActionButtonSpec[]) => {
    const { t } = this.props;

    if (buttons) {
      return <TitleActionButtonRow buttons={buttons} />;
    }
  };

  render() {
    const { logoPath } = this.props;
    const logo = logoPath ? getByPath(this, logoPath) : undefined;

    return (
      <div className={styles.pageTitleComponent}>
        {this.renderTitleText()}
        <div className={styles.buttonsWrapper}>{this.renderLeftButtons()}</div>

        <div style={{ flex: 1 }} />

        {logo && <img src={logo} />}
        <div className={styles.buttonsWrapper}>{this.renderRightButtons()}</div>
      </div>
    );
  }
}

const getByPath = (obj: Object, path: string[]) => {
  const _path = [...path];
  const part = obj[_path.shift()];

  if (!part) {
    return;
  }

  if (_path.length) {
    return getByPath(part, _path);
  }

  return part;
};
